/* assets/section-video-carousel.js
 * ============================================================
 * Slice 13 Phase B.1 — Video carousel rail engine.
 *
 * Loaded unconditionally with the section (settings-based gate
 * would over-restrict; page-level gating already prevents
 * shipping these bytes where no video-carousel section renders).
 * Inside the section, the rail early-returns when DOM-measured
 * cardEls.length < 2 — same idiom as section-slideshow.js.
 *
 * Conventions verbatim from section-slideshow.js / component-product-carousel.js:
 *   - 'use strict'
 *   - var, not let/const
 *   - function declarations, not arrow functions
 *   - IIFE-dedup guard per Rule J12 (window.__BLOCKDEDUP_VIDEO_CAROUSEL__)
 *   - DOMContentLoaded + shopify:section:load for hot reload
 *   - Per-section state in initRail(sectionEl) closure
 *
 * ASYMMETRIC SEAM (binding constraint):
 *   The section CANNOT read its theme-block children's
 *   block.settings or block.type from Liquid
 *   (DEV-S13-ENGINE-LOAD-SECTION-LEVEL). All per-card geometry
 *   is therefore measured from the rendered DOM:
 *   offsetWidth + getComputedStyle column-gap on actual children.
 *   The JS reads SECTION settings via data-* attributes on the
 *   section root (settings ARE Liquid-readable), never via
 *   any section.blocks introspection.
 *
 * ARCHITECTURE — transform-wrap track + inert clones.
 *   Track is a flex row; positioning is a single translateX on
 *   the track. Marquee precedent (assets/section-marquee.js)
 *   established transform-wrap as the in-repo pattern for
 *   horizontal scrollable rails. Cardstack precedent
 *   (assets/section-slideshow.js setupCardstackDrag) established
 *   Pointer Events + setPointerCapture for drag.
 *
 *   When loop is enabled, the track is rendered as:
 *     [ left clones (totalReal) | real cards (totalReal) | right clones (totalReal) ]
 *   Clones are produced by .cloneNode(true) then stripped of
 *   ALL engine hooks per Correction B
 *   (DEV-S13-ENGINE-LOAD-SECTION-LEVEL extension for B.1):
 *     - [data-video-player]        attribute removed
 *     - [data-video-player-init]   attribute removed
 *     - .video-player__sources     <script> child removed
 *     - aria-hidden="true" + tabindex="-1" added
 *     - .video-card--clone class added
 *   The frozen Phase-A engine exposes no window re-init entry
 *   point (confirmed by reading assets/component-video-player.js;
 *   sealed IIFE; only window symbol is the dedup guard
 *   __BLOCKDEDUP_VIDEO_PLAYER__). Inert clones are therefore the
 *   only safe loop primitive: the engine's initAll, which already
 *   ran on DOMContentLoaded before this rail JS runs (defer
 *   scripts execute in document order; the section file emits
 *   engine first), cannot match the cloned DOM since clones lack
 *   the [data-video-player] selector hook. Clones never play.
 *
 * ACCEPTANCE CRITERIA & SATISFACTION:
 *
 *   A1 — Tap on a card poster (pointer movement < 10px) →
 *        Phase-A engine click-to-play fires.
 *        Satisfied by: pointerdown NEVER calls preventDefault;
 *        setPointerCapture is on the TRACK, so the click event
 *        on pointerup still fires on the descendant target
 *        (e.g., .video-player); the browser only cancels the
 *        click when pointer movement exceeds ~3-5px, well below
 *        our 10px drag threshold. Real card .video-player retains
 *        its engine click listener attached at engine init time.
 *
 *   A2 — Drag (movement > 10px) → rail translates, NO playback.
 *        Satisfied by: pointermove updates inline translateX
 *        directly so the rail moves under finger/pointer; on
 *        pointerup, if |dx| >= 10px we treat as drag and call
 *        goTo(state.currentIdx + delta). The browser's own
 *        click-cancel-after-pointer-movement behavior suppresses
 *        the click event on the descendant target (so the engine
 *        does not fire on the drag-end). We never call
 *        preventDefault on pointerdown — relying on the browser
 *        for click-vs-pointer-cancel arbitration.
 *
 *   A3 — Tap the .video-player__play <button> → playback works.
 *        Satisfied by: pointerdown early-returns when
 *        e.target.closest('a, button, [role="button"], ...')
 *        matches. Pointer events on interactive descendants are
 *        therefore never captured by the track, never set
 *        dragState, and never interfere with the click handler
 *        the descendant owns.
 *
 *   B1 — Boundary crossing → seamless silent reposition.
 *        Satisfied by: a transitionend listener on the track
 *        watches for the boundary-crossing transition to
 *        complete, then requestAnimationFrame schedules the
 *        silent snap-back to the equivalent in-range idx in
 *        the next paint window. transition is set to 'none'
 *        for the snap-back positionTrack call. The visual
 *        frame at the end of the animated transition is
 *        identical to the frame at the start of the silent
 *        snap because clones are exact .cloneNode(true) copies.
 *
 *   B2 — Clicking an inert clone → advance rail to the
 *        equivalent real card; clone NEVER plays.
 *        Satisfied by: clones have NO [data-video-player]
 *        attribute (stripped at clone time), so the engine
 *        cannot find them via its initAll selector. A capture-
 *        phase 'click' delegation on the track matches
 *        .video-card--clone and computes the shortest-path
 *        delta from state.currentIdx to the represented real
 *        card index, then calls goTo. The engine has no
 *        listener to fire on the clone's .video-player div.
 *
 * IF A1/A2/A3 had failed empirically, the pre-specified fallback
 * was: switch to setPointerCapture on the dragged card (cardstack
 * precedent), with a ~200ms post-drag click-suppression flag on
 * the card. Not applied here — Phase 1.5 was waived and the
 * track-capture design is preserved per the approved plan.
 * ============================================================
 */
(function () {
  'use strict';

  if (window.__BLOCKDEDUP_VIDEO_CAROUSEL__) {
    return;
  }
  window.__BLOCKDEDUP_VIDEO_CAROUSEL__ = true;

  var DRAG_THRESHOLD = 10;
  var TRANSITION_MS = 300;

  function makeInertClone(cardEl) {
    var c = cardEl.cloneNode(true);
    c.classList.add('video-card--clone');
    c.setAttribute('aria-hidden', 'true');
    c.setAttribute('tabindex', '-1');
    /* Phase B.2: strip autoplay-eligibility from clones so the IO autoplay
       layer cannot observe them. The engine hooks were already stripped
       below (data-video-player removal); this additional strip prevents
       the section JS's IO from instantiating ghost <video>s inside an
       aria-hidden clone, which would waste decoder slots against the
       DECODER_CAP. */
    c.removeAttribute('data-vc-autoplay-eligible');
    var players = c.querySelectorAll('[data-video-player]');
    var i;
    for (i = 0; i < players.length; i++) {
      players[i].removeAttribute('data-video-player');
      players[i].removeAttribute('data-video-player-init');
      var srcs = players[i].querySelector('.video-player__sources');
      if (srcs) {
        srcs.parentNode.removeChild(srcs);
      }
    }
    return c;
  }

  /* Phase D: fullscreen centered-modal layer.
   *
   * Returns null when fullscreen is disabled for this section OR when the
   * dialog DOM is absent. Otherwise returns an API object consumed by
   * initRail's click handlers:
   *   { openFullscreen(card), closeFullscreen(), isOpen(), setOnCloseCallback(fn) }
   *
   * Single source of truth for cleanup is the dialog's native 'close' event
   * handler — fires on BOTH Esc dismissal AND .close() invocation. The
   * closeFullscreen() public method just calls dialog.close(); the event
   * handler runs teardown, scroll unlock, fsIsOpen flip, and the optional
   * post-close callback (used by initRail to re-observe the autoplay IO).
   *
   * Mechanics:
   *   - Single per-section <dialog> (rendered by snippets/video-fullscreen.liquid).
   *   - Engine container inside dialog ships empty sources; we re-source per-open
   *     via the Phase D opts.sources override on the video-player:activate dispatch.
   *   - On open: snapshot all currently-playing inline players in this section and
   *     dispatch :teardown on each (mirrors the Page-Visibility pattern at L356-371).
   *   - On close: dispatch :teardown on the fullscreen player; unlock body scroll;
   *     fire the optional post-close callback (e.g., IO re-observe for autoplay).
   *   - Body scroll lock: iOS-safe position:fixed pattern with savedScrollY restore.
   *   - All listeners scoped to dialog / dialog descendants — no document-level
   *     listeners. Customizer hot-reload cleanup via the existing
   *     sectionEl.__videoCarouselCleanup orchestration (extended below).
   */
  function initFullscreen(sectionEl) {
    var fullscreenEnabled = sectionEl.getAttribute('data-vc-fullscreen-enabled') === 'true';
    if (!fullscreenEnabled) return null;

    var dialog = sectionEl.querySelector('.video-fullscreen');
    if (!dialog) return null;

    var fullscreenPlayer = dialog.querySelector('.video-fullscreen__player');
    var closeBtn = dialog.querySelector('[data-video-fullscreen-close]');
    if (!fullscreenPlayer || !closeBtn) return null;

    var fsIsOpen = false;
    var savedScrollY = 0;
    var _onCloseCallback = null;

    function lockBodyScroll() {
      savedScrollY = window.scrollY || window.pageYOffset || 0;
      document.body.style.position = 'fixed';
      document.body.style.insetBlockStart = '-' + savedScrollY + 'px';
      document.body.style.inlineSize = '100%';
      document.body.classList.add('video-fullscreen-open');
    }

    function unlockBodyScroll() {
      document.body.style.position = '';
      document.body.style.insetBlockStart = '';
      document.body.style.inlineSize = '';
      document.body.classList.remove('video-fullscreen-open');
      window.scrollTo(0, savedScrollY);
    }

    function getCardSources(card) {
      var sourcesScript = card.querySelector('.video-player__sources');
      if (!sourcesScript) return null;
      try {
        var arr = JSON.parse(sourcesScript.textContent || '[]');
        return (Array.isArray(arr) && arr.length > 0) ? arr : null;
      } catch (err) {
        return null;
      }
    }

    function teardownAllInlineInSection() {
      /* Snapshot first — dispatching teardown removes the is-playing class
         which would mutate the live NodeList mid-iteration if not snapshotted.
         Pattern mirrors L358-360 of the Page-Visibility branch. Excludes the
         fullscreen container itself so it never self-teardowns. */
      var playing = sectionEl.querySelectorAll('.video-player.is-playing');
      var arr = Array.prototype.slice.call(playing);
      var i;
      for (i = 0; i < arr.length; i++) {
        if (arr[i] === fullscreenPlayer) continue;
        try { arr[i].dispatchEvent(new CustomEvent('video-player:teardown')); }
        catch (err) { /* benign */ }
      }
    }

    function openFullscreen(card) {
      if (fsIsOpen) return;
      if (!card) return;
      var sources = getCardSources(card);
      if (!sources) return;

      /* Per-card aspect-ratio set as CSS custom property; CSS consumes via
         aspect-ratio: var(--vfs-aspect, 9 / 16). aspect-ratio accepts a
         decimal width/height directly. */
      var aspectAttr = card.getAttribute('data-video-aspect');
      var aspect = parseFloat(aspectAttr);
      if (!isFinite(aspect) || aspect <= 0) aspect = 0.5625;
      fullscreenPlayer.style.setProperty('--vfs-aspect', String(aspect));

      /* Decoder discipline: tear down all inline players before opening so
         fullscreen's single active video sits well below the cap of 3. */
      teardownAllInlineInSection();

      /* Open. PB1: showModal() is the 2026-baseline path; F1 fallback
         specified in spec §14. */
      try {
        dialog.showModal();
      } catch (err) {
        dialog.setAttribute('open', '');
      }
      lockBodyScroll();
      fsIsOpen = true;

      /* Re-source the engine container via the Phase D opts.sources override.
         Engine's activate listener calls activate({muted: false, controls: true,
         sources: <these>}) — unmuted because we're inside a user-gesture
         click path (PB2). */
      fullscreenPlayer.dispatchEvent(new CustomEvent('video-player:activate', {
        detail: { muted: false, controls: true, sources: sources }
      }));
    }

    /* Public close — defers all cleanup to the 'close' event handler so
       Esc dismissal and programmatic close share one cleanup path. */
    function closeFullscreen() {
      if (!fsIsOpen) return;
      try { dialog.close(); }
      catch (err) { dialog.removeAttribute('open'); }
    }

    function isOpen() { return fsIsOpen; }

    function setOnCloseCallback(fn) { _onCloseCallback = fn; }

    /* Dismiss path: X button. */
    closeBtn.addEventListener('click', function (e) {
      e.preventDefault();
      closeFullscreen();
    });

    /* Dismiss path: backdrop click. Fires on dialog itself; inner clicks
       (video controls, close button) don't bubble up with target === dialog. */
    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) closeFullscreen();
    });

    /* SINGLE SOURCE OF TRUTH for cleanup. Fires on:
       - Native Esc dismissal (after the dispatched 'cancel' event).
       - dialog.close() called from closeFullscreen() (X button, backdrop,
         cleanup hook).
       PB6 verified — both paths fire 'close'. */
    dialog.addEventListener('close', function () {
      if (!fsIsOpen) return;
      try { fullscreenPlayer.dispatchEvent(new CustomEvent('video-player:teardown')); }
      catch (err) { /* benign */ }
      unlockBodyScroll();
      fsIsOpen = false;
      if (_onCloseCallback) {
        try { _onCloseCallback(); } catch (err) { /* benign */ }
      }
    });

    return {
      openFullscreen: openFullscreen,
      closeFullscreen: closeFullscreen,
      isOpen: isOpen,
      setOnCloseCallback: setOnCloseCallback
    };
  }

  function initRail(sectionEl) {
    /* Phase B.2 cleanup-on-reinit: if a prior closure stored a cleanup
       function on this section element (customizer hot-reload, etc.), call
       it before doing anything else. The cleanup clears the vcRailInit
       dataset flag so the idempotency guard below does not short-circuit
       this fresh init pass. If no cleanup exists this is a no-op. */
    if (sectionEl.__videoCarouselCleanup) {
      try { sectionEl.__videoCarouselCleanup(); } catch (e) { /* benign */ }
    }

    if (sectionEl.dataset.vcRailInit === 'true') {
      return;
    }
    sectionEl.dataset.vcRailInit = 'true';

    var trackEl = sectionEl.querySelector('[data-vc-track]');
    if (!trackEl) {
      return;
    }

    /* Phase D: fullscreen layer init. Returns null when disabled or DOM
       absent; otherwise returns { openFullscreen, closeFullscreen, isOpen,
       setOnCloseCallback }. Lives BEFORE the totalReal early-return so
       single-card sections still get fullscreen-on-click (spec §13 A13). */
    var fs = initFullscreen(sectionEl);

    /* Phase D: real-card click delegation (capture phase). Fires when fs
       is non-null. Clone-card clicks are handled by the MODIFIED clone-click
       handler at the bottom of initRail (which also branches on fs). This
       handler covers REAL cards only — clones are filtered out. */
    if (fs) {
      trackEl.addEventListener('click', function (e) {
        if (fs.isOpen()) return;  /* dialog handles its own clicks while open */
        var card = (e.target && e.target.closest) ? e.target.closest('.video-card') : null;
        if (!card) return;
        if (card.classList.contains('video-card--clone')) return;  /* handled below */
        /* Skip clicks on interactive descendants — let buttons/links act
           normally. EXCEPTION (Phase D hotfix #2): .video-player__play is
           the primary affordance for opening fullscreen when vc_enable_fullscreen
           is on; play-button clicks route to fs.openFullscreen instead of
           inline-activating via the engine's bubble-phase click listener.
           Mute toggle (.video-player__mute-toggle) and any other button
           descendants still match 'button:not(.video-player__play)' and
           preserve their normal click behavior. */
        if (e.target.closest && e.target.closest('a, button:not(.video-player__play), [role="button"], input, textarea, select')) {
          return;
        }
        e.stopPropagation();
        e.preventDefault();
        fs.openFullscreen(card);
      }, true);
    }

    var realCards = Array.prototype.slice.call(trackEl.querySelectorAll('.video-card'));
    var totalReal = realCards.length;
    if (totalReal < 2) {
      return;
    }

    /* ---- LAYOUT MODE DETECTION (Phase B.1 bug-fix correction) ----
       Init-time decision: single matchMedia check, NO listener.
       Defaults to grid/grid in the schema, so out of the box the section
       renders as a static wrap-grid — fixes the reported bug where the
       3-card preset + visible_count_desktop=5 inits and clones a row of
       duplicates. Carousel is opt-in per viewport.

       KNOWN LIMITATION (B.1): window resize across 750px without page
       reload does NOT re-init. Deliberate scoping choice — a listener
       would have to clone-rebuild, cancel any in-flight drag, and
       reset state on every breakpoint flip. Pages reload often enough
       in practice that the simpler init-time decision is the right
       trade-off for B.1. */
    var isDesktop = window.matchMedia('(min-width: 750px)').matches;
    var layoutMode = isDesktop
      ? sectionEl.getAttribute('data-vc-layout-desktop')
      : sectionEl.getAttribute('data-vc-layout-mobile');
    var carouselActive = layoutMode === 'carousel';

    /* ============================================================
       PHASE B.2 — AUTOPLAY / PERF LAYER
       ============================================================
       Runs in BOTH grid and carousel mode (must be initialized
       before the grid early-return). Three gates skip the entire
       layer: autoplay disabled, prefers-reduced-motion, or
       Save-Data. Click-to-play continues to work via the engine's
       own click listener regardless.

       Event flow:
         IO entering  -> activateAutoplay -> dispatch :activate
                       -> engine builds <video muted controls=false>
                       -> (post-dispatch) videoEl.muted = rememberedMuteState
         IO leaving   -> teardownAutoplay -> dispatch :teardown
                       -> engine releases decoder + removes element
         visibilityX  -> hidden: snapshot+teardown all; clear queue
                      -> visible: disconnect + re-observe to force
                                  fresh IO callbacks
         mute click   -> section-delegated: stopPropagation, flip
                         videoEl.muted, flip rememberedMuteState,
                         update aria.
       ============================================================ */
    var DECODER_CAP = 3;
    var autoplayEnabled = sectionEl.getAttribute('data-vc-autoplay-enabled') === 'true';
    var mutePolicy = sectionEl.getAttribute('data-vc-mute-policy') || 'user_controllable';
    var apReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var saveData = false;
    try {
      saveData = !!(navigator.connection && navigator.connection.saveData);
    } catch (err) { /* benign — older browsers */ }

    var autoplayInstances = null;
    var pendingQueue = null;
    var rememberedMuteState = true;
    var ioInstance = null;
    var apHandleVisibility = null;
    var apMuteDelegate = null;

    function apIsCurrentlyVisible(cardEl, root) {
      var cardRect = cardEl.getBoundingClientRect();
      var rootRect;
      if (root) {
        rootRect = root.getBoundingClientRect();
      } else {
        rootRect = { top: 0, left: 0, right: window.innerWidth, bottom: window.innerHeight };
      }
      var verticalOverlap = Math.max(0, Math.min(cardRect.bottom, rootRect.bottom) - Math.max(cardRect.top, rootRect.top));
      var horizontalOverlap = Math.max(0, Math.min(cardRect.right, rootRect.right) - Math.max(cardRect.left, rootRect.left));
      var area = cardRect.width * cardRect.height;
      if (area === 0) return false;
      var overlapArea = verticalOverlap * horizontalOverlap;
      return (overlapArea / area) >= 0.5;
    }

    function apUpdateMuteToggleAria(container, isMuted) {
      var toggleBtn = container.querySelector('.video-player__mute-toggle');
      if (!toggleBtn) return;
      toggleBtn.setAttribute('aria-pressed', String(isMuted));
      var newLabel = isMuted
        ? sectionEl.getAttribute('data-vc-unmute-label')
        : sectionEl.getAttribute('data-vc-mute-label');
      if (newLabel) toggleBtn.setAttribute('aria-label', newLabel);
    }

    function apActivate(container) {
      container.dispatchEvent(new CustomEvent('video-player:activate', {
        detail: { muted: true, controls: false }
      }));
      autoplayInstances.add(container);
      /* Engine's activate() is synchronous through appendChild, so the
         <video> exists by the time dispatch returns. Apply remembered
         mute state when policy allows user toggle. */
      if (mutePolicy === 'user_controllable') {
        var videoEl = container.querySelector('video');
        if (videoEl) {
          videoEl.muted = rememberedMuteState;
          apUpdateMuteToggleAria(container, videoEl.muted);
        }
      }
    }

    function apTeardown(container) {
      container.dispatchEvent(new CustomEvent('video-player:teardown'));
      autoplayInstances.delete(container);
    }

    function apPromoteFromPendingQueue(ioRoot) {
      while (pendingQueue.length > 0 && autoplayInstances.size < DECODER_CAP) {
        var next = pendingQueue.shift();
        var card = next.closest ? next.closest('.video-card') : null;
        if (card && apIsCurrentlyVisible(card, ioRoot)) {
          apActivate(next);
        }
      }
    }

    if (autoplayEnabled && !apReducedMotion && !saveData && ('IntersectionObserver' in window)) {
      autoplayInstances = new Set();
      pendingQueue = [];

      var railEl = sectionEl.querySelector('[data-vc-rail]');
      var ioRoot = carouselActive ? railEl : null;
      var ioOpts = { root: ioRoot, threshold: 0.5 };

      ioInstance = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var card = entry.target;
          var container = card.querySelector('[data-video-player]');
          if (!container) return;

          if (entry.isIntersecting) {
            if (autoplayInstances.has(container)) return;
            if (autoplayInstances.size >= DECODER_CAP) {
              if (pendingQueue.indexOf(container) === -1) {
                pendingQueue.push(container);
              }
              return;
            }
            apActivate(container);
          } else {
            if (autoplayInstances.has(container)) {
              apTeardown(container);
              apPromoteFromPendingQueue(ioRoot);
            } else {
              var idx = pendingQueue.indexOf(container);
              if (idx !== -1) pendingQueue.splice(idx, 1);
            }
          }
        });
      }, ioOpts);

      var eligibleCards = Array.prototype.slice.call(
        sectionEl.querySelectorAll('.video-card[data-vc-autoplay-eligible]')
      );
      eligibleCards.forEach(function (card) { ioInstance.observe(card); });

      /* Phase D: when the fullscreen layer is wired AND the autoplay layer
         is active, register a post-close callback that re-observes the IO.
         Mirrors the Page-Visibility-visible branch (L362-369): disconnect +
         re-observe forces fresh IO callbacks so eligible visible cards
         re-instantiate inline playback after the modal closes. */
      if (fs) {
        fs.setOnCloseCallback(function () {
          if (!ioInstance) return;
          var cards = Array.prototype.slice.call(
            sectionEl.querySelectorAll('.video-card[data-vc-autoplay-eligible]')
          );
          ioInstance.disconnect();
          cards.forEach(function (card) { ioInstance.observe(card); });
        });
      }

      /* Mute toggle delegation. The button itself is emitted by Liquid only
         when mute_policy == 'user_controllable'. We still bind unconditionally
         when the autoplay layer is active; toggle-button absence makes the
         handler a no-op via the closest() lookup. */
      apMuteDelegate = function (e) {
        var toggleBtn = e.target.closest ? e.target.closest('.video-player__mute-toggle') : null;
        if (!toggleBtn) return;
        e.stopPropagation();
        var container = toggleBtn.closest('[data-video-player]');
        if (!container) return;
        var videoEl = container.querySelector('video');
        if (!videoEl) return;
        videoEl.muted = !videoEl.muted;
        rememberedMuteState = videoEl.muted;
        apUpdateMuteToggleAria(container, videoEl.muted);
      };
      sectionEl.addEventListener('click', apMuteDelegate);

      /* Page-Visibility: tear down all autoplay-instantiated videos when
         the tab is hidden; re-observe to force fresh IO callbacks when the
         tab returns. Click-instantiated videos (NOT in autoplayInstances)
         are untouched. */
      apHandleVisibility = function () {
        if (document.hidden) {
          var snapshot = [];
          autoplayInstances.forEach(function (c) { snapshot.push(c); });
          snapshot.forEach(apTeardown);
          pendingQueue.length = 0;
        } else {
          if (ioInstance) {
            var cards = Array.prototype.slice.call(
              sectionEl.querySelectorAll('.video-card[data-vc-autoplay-eligible]')
            );
            ioInstance.disconnect();
            cards.forEach(function (card) { ioInstance.observe(card); });
          }
        }
      };
      document.addEventListener('visibilitychange', apHandleVisibility);
    }

    /* Cleanup function for customizer hot-reload / shopify:section:load
       re-entry. Stored on sectionEl per the spec; called from the top of
       initRail before the idempotency guard. Clears the vcRailInit dataset
       flag so the new init pass can fully proceed. */
    sectionEl.__videoCarouselCleanup = function () {
      if (ioInstance) {
        ioInstance.disconnect();
        ioInstance = null;
      }
      if (autoplayInstances) {
        autoplayInstances.forEach(function (c) {
          try { c.dispatchEvent(new CustomEvent('video-player:teardown')); } catch (e) { /* benign */ }
        });
        autoplayInstances.clear();
      }
      if (pendingQueue) {
        pendingQueue.length = 0;
      }
      if (apHandleVisibility) {
        document.removeEventListener('visibilitychange', apHandleVisibility);
        apHandleVisibility = null;
      }
      if (apMuteDelegate) {
        sectionEl.removeEventListener('click', apMuteDelegate);
        apMuteDelegate = null;
      }

      /* Phase D: if a dialog is open at section-reload time, close it so
         body scroll restores cleanly. The 'close' event handler runs
         teardown + unlock synchronously, so the body doesn't stay
         position:fixed when the new section markup arrives. */
      if (fs && fs.isOpen()) {
        try { fs.closeFullscreen(); }
        catch (err) { /* benign */ }
      }

      sectionEl.removeAttribute('data-vc-rail-init');
    };

    if (!carouselActive) {
      /* Grid mode for current viewport. The CSS handles flex-wrap and
         chrome hiding via .section-video-carousel--grid-{desktop|mobile}
         ancestor selectors. The rail JS does nothing further — no
         clones, no event bindings, no drag setup, no inline transform.
         The Phase-A engine click-to-play is unaffected (it binds on
         its own DOMContentLoaded against [data-video-player] elements
         that exist regardless of the rail). */
      return;
    }

    var enableLoop = sectionEl.getAttribute('data-vc-loop') === 'true';
    var enableDrag = sectionEl.getAttribute('data-vc-drag') === 'true';
    var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var prevBtn = sectionEl.querySelector('[data-vc-prev]');
    var nextBtn = sectionEl.querySelector('[data-vc-next]');
    var dotsEl = sectionEl.querySelector('[data-vc-dots]');

    /* ---- LOOP-MAKES-SENSE GUARD ----
       Carousel mode active for this viewport. If totalReal <= visibleCount,
       ALL cards already fit the viewport — looping would only show
       duplicates with no scroll-distance to hide them, and the chrome
       (arrows/dots) would have nowhere to navigate to. Skip the entire
       rail setup and hide the non-functional chrome to match grid mode's
       visual cleanliness. */
    var visibleCount = parseInt(
      sectionEl.getAttribute(isDesktop ? 'data-vc-visible-desktop' : 'data-vc-visible-mobile'),
      10
    ) || (isDesktop ? 5 : 2);
    var loopMakesSense = totalReal > visibleCount;
    var shouldClone = enableLoop && loopMakesSense;

    if (!loopMakesSense) {
      var arrowsWrapper = sectionEl.querySelector('[data-vc-arrows]');
      if (arrowsWrapper) arrowsWrapper.style.display = 'none';
      if (dotsEl) dotsEl.style.display = 'none';
      return;
    }

    var state = {
      currentIdx: 0,
      snapPending: false
    };

    /* ---- Inert clone insertion (Correction B) ---- */
    if (shouldClone) {
      var leftClones = realCards.map(makeInertClone);
      var rightClones = realCards.map(makeInertClone);
      var i;
      for (i = 0; i < leftClones.length; i++) {
        trackEl.insertBefore(leftClones[i], realCards[0]);
      }
      for (i = 0; i < rightClones.length; i++) {
        trackEl.appendChild(rightClones[i]);
      }
    }

    /* ---- DOM measurement (asymmetric seam) ---- */
    function measureStep() {
      var first = realCards[0];
      if (!first) return 0;
      var trackStyle = window.getComputedStyle(trackEl);
      var gap = parseFloat(trackStyle.columnGap || trackStyle.gap) || 0;
      return first.offsetWidth + gap;
    }

    function currentTranslateX() {
      var t = trackEl.style.transform || '';
      var m = /translateX\(([-\d.]+)px\)/.exec(t);
      return m ? parseFloat(m[1]) : 0;
    }

    function positionTrack(idx, animate) {
      var step = measureStep();
      var offsetReal = enableLoop ? totalReal : 0;
      var offset = -(offsetReal + idx) * step;
      trackEl.style.transition = (animate && !prefersReducedMotion)
        ? ('transform ' + TRANSITION_MS + 'ms ease-out')
        : 'none';
      trackEl.style.transform = 'translateX(' + offset + 'px)';
    }

    function realIdxFor(idx) {
      return ((idx % totalReal) + totalReal) % totalReal;
    }

    /* ---- Dots: populate one per real card ---- */
    var dotEls = [];
    if (dotsEl) {
      var d;
      for (d = 0; d < totalReal; d++) {
        var li = document.createElement('li');
        li.setAttribute('role', 'presentation');
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'section-video-carousel__dot';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('data-vc-dot', '');
        dot.setAttribute('data-vc-dot-index', String(d));
        dot.setAttribute('aria-label', String(d + 1));
        dot.setAttribute('aria-selected', d === 0 ? 'true' : 'false');
        dot.setAttribute('tabindex', d === 0 ? '0' : '-1');
        li.appendChild(dot);
        dotsEl.appendChild(li);
        dotEls.push(dot);
      }
    }

    function updateArrowDisabled() {
      if (!prevBtn || !nextBtn) return;
      if (enableLoop) {
        prevBtn.disabled = false;
        nextBtn.disabled = false;
        return;
      }
      prevBtn.disabled = (state.currentIdx <= 0);
      nextBtn.disabled = (state.currentIdx >= totalReal - 1);
    }

    function updateDotsActive() {
      if (dotEls.length === 0) return;
      var real = realIdxFor(state.currentIdx);
      var i;
      for (i = 0; i < dotEls.length; i++) {
        var active = (i === real);
        dotEls[i].setAttribute('aria-selected', active ? 'true' : 'false');
        dotEls[i].setAttribute('tabindex', active ? '0' : '-1');
        dotEls[i].classList.toggle('section-video-carousel__dot--active', active);
      }
    }

    /* ---- Navigation ---- */
    function goTo(idx, fromUserAction) {
      if (!enableLoop) {
        if (idx < 0) idx = 0;
        if (idx >= totalReal) idx = totalReal - 1;
      }
      state.currentIdx = idx;
      positionTrack(idx, true);
      updateDotsActive();
      updateArrowDisabled();
      if (enableLoop && (idx < 0 || idx >= totalReal)) {
        state.snapPending = true;
      }
    }

    function next() { goTo(state.currentIdx + 1, true); }
    function prev() { goTo(state.currentIdx - 1, true); }

    function shortestDelta(targetReal) {
      var currentReal = realIdxFor(state.currentIdx);
      var delta = targetReal - currentReal;
      if (enableLoop) {
        if (delta > totalReal / 2) delta -= totalReal;
        if (delta < -totalReal / 2) delta += totalReal;
      }
      return delta;
    }

    /* ---- Boundary snap (B1: rAF for visible-seamless snap) ---- */
    trackEl.addEventListener('transitionend', function (e) {
      if (e.propertyName !== 'transform') return;
      if (!state.snapPending) return;
      state.snapPending = false;
      requestAnimationFrame(function () {
        var equivalent = realIdxFor(state.currentIdx);
        state.currentIdx = equivalent;
        positionTrack(equivalent, false);
      });
    });

    /* ---- Arrows ---- */
    if (prevBtn) prevBtn.addEventListener('click', prev);
    if (nextBtn) nextBtn.addEventListener('click', next);

    /* ---- Dot click ---- */
    var di;
    for (di = 0; di < dotEls.length; di++) {
      (function (dotEl) {
        dotEl.addEventListener('click', function () {
          var idx = parseInt(dotEl.getAttribute('data-vc-dot-index'), 10);
          if (isNaN(idx)) return;
          goTo(state.currentIdx + shortestDelta(idx), true);
        });
      })(dotEls[di]);
    }

    /* ---- Clone click delegation (B2: clones advance the rail
       to their equivalent real card; they never play because
       they have no engine hooks). Capture phase so we observe
       the click before any descendant bubbling listener could
       interfere. NO preventDefault — engine has no listener on
       the clone's .video-player div anyway (no [data-video-player]).
       Real card clicks fall through to the engine via the bubble
       path; they do not match .video-card--clone here so this
       handler no-ops on them. ---- */
    trackEl.addEventListener('click', function (e) {
      if (fs && fs.isOpen()) return;  /* dialog handles its own clicks */
      var clone = (e.target && e.target.closest) ? e.target.closest('.video-card--clone') : null;
      if (!clone) return;
      var allCards = Array.prototype.slice.call(trackEl.querySelectorAll('.video-card'));
      var cardIdx = allCards.indexOf(clone);
      if (cardIdx < 0) return;
      var realRepresented;
      if (cardIdx < totalReal) {
        realRepresented = cardIdx;
      } else if (cardIdx < totalReal * 2) {
        realRepresented = cardIdx - totalReal;
      } else {
        realRepresented = cardIdx - totalReal * 2;
      }
      /* Phase D: when fullscreen is enabled, clone-click opens fullscreen
         using the represented real card's sources (the realCards array is
         in closure scope from the realCards assignment above). When
         disabled, clone-click retains B.1's advance-rail behavior. Option
         C per plan §4.1. */
      if (fs) {
        var realCard = realCards[realRepresented];
        if (realCard) {
          e.stopPropagation();
          e.preventDefault();
          fs.openFullscreen(realCard);
          return;
        }
      }
      goTo(state.currentIdx + shortestDelta(realRepresented), true);
    }, true);

    /* ---- Pointer-drag (A1/A2/A3) ----
       setPointerCapture on the TRACK (not on the card) preserves
       click events on descendant interactive elements when the
       drag stays below threshold. NEVER calls preventDefault on
       pointerdown. Descendant-interactive early-return preserves
       (A3) the engine's play <button> path. ---- */
    if (enableDrag) {
      var drag = {
        active: false,
        startX: 0,
        dragDx: 0,
        startTransform: 0,
        pointerId: null
      };

      trackEl.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        if (e.target.closest && e.target.closest('a, button, [role="button"], input, textarea, select')) {
          return;
        }
        drag.active = true;
        drag.startX = e.clientX;
        drag.dragDx = 0;
        drag.startTransform = currentTranslateX();
        drag.pointerId = e.pointerId;
        try {
          trackEl.setPointerCapture(e.pointerId);
        } catch (err) { /* setPointerCapture can throw on edge browsers; non-fatal */ }
      });

      trackEl.addEventListener('pointermove', function (e) {
        if (!drag.active || e.pointerId !== drag.pointerId) return;
        drag.dragDx = e.clientX - drag.startX;
        trackEl.style.transition = 'none';
        trackEl.style.transform = 'translateX(' + (drag.startTransform + drag.dragDx) + 'px)';
      });

      function endDrag(e) {
        if (!drag.active || e.pointerId !== drag.pointerId) return;
        drag.active = false;
        var dx = drag.dragDx;
        drag.dragDx = 0;
        if (Math.abs(dx) < DRAG_THRESHOLD) {
          /* Tap: snap back to current idx in case any sub-threshold
             drift was applied to the transform. Browser will fire
             the click event on the descendant target after pointerup
             since we never preventDefault'd and the movement was
             under the browser's own click-cancel threshold. */
          positionTrack(state.currentIdx, true);
          return;
        }
        var step = measureStep();
        var delta = (step > 0) ? Math.round(-dx / step) : 0;
        if (delta === 0) delta = (dx < 0) ? 1 : -1;
        goTo(state.currentIdx + delta, true);
      }
      trackEl.addEventListener('pointerup', endDrag);
      trackEl.addEventListener('pointercancel', endDrag);

      /* Block native image / text drag-ghost on the track. */
      trackEl.addEventListener('dragstart', function (e) {
        e.preventDefault();
      });
    }

    /* ---- ResizeObserver: re-position on container resize so
       breakpoint flips (visible-count desktop↔mobile) re-align
       the track at the current idx. Cleaned up automatically
       when Shopify replaces the section DOM in the customizer. ---- */
    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(function () {
        positionTrack(state.currentIdx, false);
      });
      ro.observe(sectionEl);
    }

    /* ---- Initial reconcile ---- */
    positionTrack(0, false);
    updateDotsActive();
    updateArrowDisabled();
  }

  /* A11Y (P2.3 Q-3): set the per-slide "X sur N" aria-label on every REAL card
     (clones are already aria-hidden via makeInertClone). Runs in BOTH grid and
     carousel modes — independent of initRail's <2-card early-return. Reads the
     localized template staged on the rail (data-vc-slide-label-tpl) and swaps the
     __CURRENT__/__TOTAL__ placeholders, mirroring section-slideshow.js:127-134. */
  function labelCards(sectionEl) {
    var railEl = sectionEl.querySelector('[data-vc-rail]');
    if (!railEl) return;
    var tpl = railEl.getAttribute('data-vc-slide-label-tpl') || '';
    if (!tpl) return;
    var cards = sectionEl.querySelectorAll('.video-card:not(.video-card--clone)');
    var total = cards.length;
    var i;
    for (i = 0; i < cards.length; i++) {
      cards[i].setAttribute(
        'aria-label',
        tpl.replace(/__CURRENT__/g, String(i + 1)).replace(/__TOTAL__/g, String(total))
      );
    }
  }

  function initAll(scopeEl) {
    var root = scopeEl || document;
    var sections = root.querySelectorAll('.section-video-carousel');
    var i;
    for (i = 0; i < sections.length; i++) {
      labelCards(sections[i]);
      initRail(sections[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target) {
      initAll(event.target);
    }
  });
})();
